import { Divider } from "@drone-js/core";
import { Title } from "apps/batchOnboarding/Title";
import { VStack } from "apps/commons/components/Stack";
import { AcceptTermsAndConditions } from "apps/oneOnboarding/contract/AcceptTermsAndConditions";
import { InternalUserNotification } from "apps/oneOnboarding/contract/InternalUserNotification";
import { Note } from "apps/oneOnboarding/contract/Note";
import { useOnboardingPageContextAtomValue } from "apps/oneOnboarding/onboardingPageContextAtom";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import { batchPublishBinder } from "libs/analytics/binders/batchOnboarding/batchPublish";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { Fragment } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { BulkContractCheckboxController } from "./BulkContractCheckboxController";
import { ContentPolicyAgreement } from "./ContentPolicyAgreement";
import { PropertiesOverview } from "./PropertiesOverview";
import { PropertyLicensePartialModal } from "./PropertyLicensePartialModal";
import { PropertyLicenseRequiredModal } from "./PropertyLicenseRequiredModal";
import { PropertyLicenseRequiredNotification } from "./PropertyLicenseRequiredNotification";
import { TaxFields } from "./TaxFields";
import { usePropertyLicenseValidation } from "./usePropertyLicenseValidation";

export const ContractPage = () => {
    const {
        cmsData: {
            publish: { publish },
        },
    } = useStaticDataAtomValue();
    const { isInternalUser } = useOnboardingPageContextAtomValue() || {};

    const { control } = useFormContext<BulkPropertyFormData>();
    const { fields, remove } = useFieldArray({ control, name: "propertyDataList" });

    const {
        isLicenseRequiredModalOpen,
        onCloseLicenseRequiredModal,
        isLicensePartialModalOpen,
        onCloseLicensePartialModal,
        failedProperties,
        handleContinueAnyway,
    } = usePropertyLicenseValidation(remove);

    useAnalytics(batchPublishBinder);

    const content = (
        <Fragment>
            <PropertiesOverview properties={fields} />
            <TaxFields fields={fields} />
            <Divider direction="horizontal" color="base.subtle" />
            <AcceptTermsAndConditions checkbox={<BulkContractCheckboxController />} />
            <ContentPolicyAgreement />
            <Note />
        </Fragment>
    );

    return (
        <VStack gap="24" pb="32">
            <Title title={publish} hideSaveAndExit />
            {isInternalUser && <InternalUserNotification />}
            <PropertyLicenseRequiredNotification />
            {content}
            <PropertyLicenseRequiredModal
                open={isLicenseRequiredModalOpen}
                onAcknowledge={onCloseLicenseRequiredModal}
            />
            <PropertyLicensePartialModal
                open={isLicensePartialModalOpen}
                onAddLicense={onCloseLicensePartialModal}
                onContinueAnyway={handleContinueAnyway}
                failedProperties={failedProperties.map((p) => p.property)}
            />
        </VStack>
    );
};
